import * as React from 'react';
import { UploadedFile } from 'app/area/case/features';
import { RemoteBlob } from 'ui/remote-blob';
import * as Hammer from 'hammerjs';

import styles from './attachment-viewer.scss';
import { FixedDocument } from 'app/components/fixed-document';
import { FullscreenModalHeader } from 'ui/fullscreen-modal-header/fullscreen-modal-header';
import { FileItemProgress } from 'ui/file-uploader/file-uploader-item-progress';
import { SlideArrowSymbol } from 'ui/common/icons/slide-arrow';
import { keyCode } from 'ui/common/key-code-types';
import { ObjectFitImage } from 'ui/object-fit-image';

export interface AttachmentViewerProps {
  createDownloader?: (blobId: number, name: string) => Promise<void>;
  loadBlob?: (blobId: number, thumbnail?: boolean) => Promise<Blob>;
  uploadedFiles?: UploadedFile[];
  onClose: () => void;
  initialSelected?: number;
}

export interface AttachmentViewerState {
  selected?: UploadedFile;
  files?: UploadedFile[];
  prevInitialSelected?: number;
}

const createLoader = (loadBlob, id, thumbnail = false) => () => {
  return loadBlob(id, thumbnail);
};

const AttachmentThumbnail = ({loadBlob, item, onClick, onLoad, selected}) => {
  const clicker = () => onClick(item);
  return <RemoteBlob onLoad={onLoad} loadBlob={loadBlob && createLoader(loadBlob, item.blobId, true)}>
    {({ objectUrl, error }) =>
    error || !objectUrl ? null : (
      <button type="button" className={`${styles.thumbnail}`} tabIndex={0} aria-selected={selected} onClick={clicker}>
        <ObjectFitImage className={styles.imageContainer} src={objectUrl} alt={item.name} cover={true}/>
      </button>
    )}
  </RemoteBlob>;
};

const findImages = (files:UploadedFile[]):UploadedFile[] => {
  return files && files.filter(item => item.type && item.type.indexOf('image') !== -1);
};
export class AttachmentViewer extends React.PureComponent<AttachmentViewerProps, AttachmentViewerState> {

  private imageContainerRef: React.RefObject<HTMLDivElement> = React.createRef();
  private thumbnailContainerRef: React.RefObject<HTMLDivElement> = React.createRef();
  private manager:HammerManager;
  public state:Readonly<AttachmentViewerState> = {
    files: [],
    selected: null
  };

  public static getDerivedStateFromProps(props:AttachmentViewerProps, state:AttachmentViewerState) {
    const attachments = findImages(props.uploadedFiles);

    if(state.prevInitialSelected !== props.initialSelected) {
      const selected = attachments.find(item => item.id === props.initialSelected);
      return {files: attachments, selected: selected, prevInitialSelected: props.initialSelected};
    }

    return {files: attachments, prevInitialSelected: props.initialSelected};
  }

  constructor (props:AttachmentViewerProps) {
    super(props);

    if (props.uploadedFiles) {
      const files = findImages(props.uploadedFiles);
      const selected = files.find(item => item.id === props.initialSelected);
      this.state = {files, selected};
    }
  }

  public componentDidMount() {
    this.setupListeners();
  }

  public componentWillUnmount () {
    this.removeListeners();
  }

  public componentDidUpdate() {
    this.autoScrollThumbnails();
    this.setupListeners();

  }

  private setupListeners = () => {
    if (this.imageContainerRef.current && !this.manager) {
      this.manager = new Hammer.Manager(this.imageContainerRef.current, {
        recognizers: [[Hammer.Swipe,{ direction: Hammer.DIRECTION_HORIZONTAL }]]
      });
      this.manager.on('swipe', this.onSwipe);
      document.addEventListener('keydown', this.onKeyDown);
    }
  };

  private removeListeners = () => {
    if (this.manager) {
      this.manager.destroy();
      this.manager = null;
      document.removeEventListener('keydown', this.onKeyDown);
    }
  };

  private selectImage = (item:UploadedFile) => {
    this.setState({selected: item});
  };

  private goToNext = (e:React.MouseEvent) => {
    e && e.preventDefault();
    const {selected, files} = this.state;
    const getIndex = files.indexOf(selected);

    this.setState({selected: files[(getIndex+1) % files.length]});
  };

  private goToPrev = (e:React.MouseEvent) => {
    e && e.preventDefault();
    const {selected, files} = this.state;
    const getIndex = files.indexOf(selected);
    this.setState({selected: files[(getIndex > 0) ? getIndex -1 : files.length-1]});
  };

  private onClose = () => {
    const { onClose } = this.props;
    this.setState({selected: null});
    this.removeListeners();
    onClose && onClose();
  };

  private onKeyDown = (e:KeyboardEvent) => {
    switch (e.keyCode) {
      case keyCode.ArrowRight:
        this.goToNext(null);
        break;
      case keyCode.ArrowLeft:
        this.goToPrev(null);
        break;
      case keyCode.Esc:
        this.onClose();
        break;
      default:
        break;
    }
  };

  private onThumbnailLoaded = () => {
    this.autoScrollThumbnails();
  };

  private onSwipe = (e:any) => {
    if (e.velocityX < 0) {
      this.goToNext(null);
    } else {
      this.goToPrev(null);
    }
  };

  private onThumbnailScroll = (event:any) => {
    if (event.currentTarget) {
      const container = event.currentTarget;

      const ev:WheelEvent = event.nativeEvent;
      const delta = ev.deltaY;

      container.scrollLeft += delta;
    }
  };

  private autoScrollThumbnails = () => {
    if (this.thumbnailContainerRef && this.thumbnailContainerRef.current) {
      const container = this.thumbnailContainerRef.current;
      const thumbnail = container.querySelector('[aria-selected="true"]');
      if (thumbnail) {
        thumbnail.scrollIntoView({behavior: 'smooth', inline: 'center'});
      }
    }
  };

  public render () {
    const { loadBlob } = this.props;
    const { files } = this.state;

    const selected = this.state && this.state.selected;
    return selected ? <FixedDocument>
      <div className={styles.viewer} tabIndex={1}>
        <FullscreenModalHeader onClose={this.onClose}>
          {selected && selected.title}
        </FullscreenModalHeader>
        <div className={`${styles.image} container`} ref={this.imageContainerRef}>
          {selected &&
            <RemoteBlob key={selected.id} loadBlob={loadBlob && createLoader(loadBlob, selected.blobId)}>
            {(props) => props.error || !props.objectUrl ?
              <div className={styles.progressContainer}><FileItemProgress indeterminate={true}/></div> : (
                <ObjectFitImage src={props.objectUrl} className={styles.fitImage} width="100%" height="100%"/>
              )
            }
            </RemoteBlob>}
          {files.length > 1 && <button className={`${styles.directionButton} ${styles.next}`} onClick={this.goToNext}>
            <SlideArrowSymbol/>
              </button> }
          {files.length > 1 && <button className={`${styles.directionButton} ${styles.prev}`} onClick={this.goToPrev}>
            <SlideArrowSymbol pointLeft={true}/>
          </button>}
        </div>
        <div className={styles.thumbnailContainer}>
          <div className={`${styles.thumbnailList} thumbnail-list container`}
              ref={this.thumbnailContainerRef} onWheel={this.onThumbnailScroll}>
            { files.map( item => <AttachmentThumbnail onLoad={this.onThumbnailLoaded} key={item.id}
              selected={selected && selected.id === item.id} loadBlob={loadBlob} item={item} onClick={this.selectImage}/>)}
          </div>
        </div>
      </div>
    </FixedDocument> : null;
  }
}
